import React, { useCallback, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import ExpansionPanelMui from '@mui/material/Accordion';
import ExpansionPanelSummaryMui from '@mui/material/AccordionSummary';
import ExpansionPanelDetailsMui from '@mui/material/AccordionDetails';
import ToggleButton from '@mui/lab/ToggleButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { SectionBase } from './SectionBase';
import { RadioWithLabel } from './RadioGroup';
import { DateContainer, KeyboardDatePicker } from './SectionDates';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SubGroup from './SubGroup';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

export const SectionRadioTree = ({
  title,
  subTitle,
  helpText,
  options: options0,
  questionId,
  value,
  onChange,
  error,
  total,
  guidanceText,
}) => {
  const [selectedSubMenu, setSelectedSubMenu] = useState();
  const options = useMemo(() => options0.map(label => label), [options0]);

  useEffect(() => {
    if (typeof value !== 'string') {
      return;
    }

    const [, treeGroup, subValue] = /(.*):\s(.*)\s\(/.exec(value);
    if (treeGroup && subValue) {
      const dates = [];
      let matches;
      for (
        const reDate = /\((\d\d\d\d-\d\d-\d\d)\)/g;
        (matches = reDate.exec(value));

      ) {
        dates.push(matches[1]);
      }
      // Creating date from '2022-05-20' causing timezone issue.
      const startDate =
        dates[0]?.length === 10 ? dates[0] + 'T00:00' : dates[0];
      const endDate = dates[1]?.length === 10 ? dates[1] + 'T00:00' : dates[1];
      onChange(questionId, {
        value: treeGroup,
        subValue,
        startDate: startDate && new Date(startDate),
        endDate: endDate && new Date(endDate),
      });
      setSelectedSubMenu(treeGroup);
    }
  }, [value, options, questionId]); // eslint-disable-line
  const handleChangeArray = useMemo(
    () =>
      options.map(({ label, responseType }) => () => {
        setSelectedSubMenu(label);
        onChange(questionId, {
          ...(typeof value === 'object' ? value : {}),
          value: label,
          startDate: undefined,
          endDate: undefined,
          subValue: responseType === 'radio' ? 'N/A' : undefined,
        });
      }),
    [questionId, value, onChange],
  ); // eslint-disable-line
  const handleSubValue = useCallback(
    subValue => {
      onChange(questionId, {
        ...(typeof value === 'object' ? value : {}),
        subValue: subValue?.target ? subValue.target.value : subValue,
      });
    },
    [value, onChange, questionId],
  );
  const handleStartDate = useCallback(
    startDate => {
      onChange(questionId, {
        ...(typeof value === 'object' ? value : {}),
        startDate,
      });
    },
    [questionId, value, onChange],
  );
  const handleEndDate = useCallback(
    endDate => {
      onChange(questionId, {
        ...(typeof value === 'object' ? value : {}),
        endDate,
      });
    },
    [questionId, value, onChange],
  );
  const handleDate = useCallback(
    startDate => {
      onChange(questionId, {
        ...(typeof value === 'object' ? value : {}),
        startDate,
      });
    },
    [questionId, value, onChange],
  );
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SectionBase {...{ subTitle, helpText, guidanceText, questionId }}>
        <div>
          {options.map(({ label, responseType, options = [] }, index) => (
            <ExpansionPanel
              key={label}
              expanded={selectedSubMenu === label}
              onChange={handleChangeArray[index]}
            >
              <ExpansionPanelSummary>
                <RadioWithLabel
                  {...{ label }}
                  checked={selectedSubMenu === label}
                />
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <SubGroup
                  options={options}
                  responseType={responseType}
                  total={total}
                  value={value?.subValue}
                  onChange={handleSubValue}
                />
              </ExpansionPanelDetails>
              {options.some(
                ({ responseType, response }) =>
                  responseType === 'date' && value?.subValue === response,
              ) && (
                <ExpansionPanelDetails>
                  <KeyboardDatePicker
                    format={'dd MMM yyyy'}
                    // emptyLabel='Select a start date'
                    placeholder="Select a start date"
                    style={{ marginLeft: '54px', marginTop: '0px' }}
                    value={value?.startDate ?? null}
                    onChange={handleDate}
                    keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
                    PopoverProps={{
                      anchorOrigin: {
                        horizontal: 'center',
                        vertical: 'bottom',
                      },
                      transformOrigin: {
                        horizontal: 'center',
                        vertical: 'top',
                      },
                    }}
                  />
                </ExpansionPanelDetails>
              )}
            </ExpansionPanel>
          ))}
          {/* Only shows datepicker if custom period is selected or there are no options, eg, is just a PERIOD */
          isOtherPeriodSelected(value) && (
            <DateContainer>
              <div>
                <TrueImpactText variant="body-bold">Start date:</TrueImpactText>
                <KeyboardDatePicker
                  format={'dd MMM yyyy'}
                  // emptyLabel='Select a start date'
                  placeholder="Select a start date"
                  value={value?.startDate ?? null}
                  onChange={handleStartDate}
                  keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
                  PopoverProps={{
                    anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                    transformOrigin: { horizontal: 'center', vertical: 'top' },
                  }}
                />
              </div>
              <div>
                <TrueImpactText variant="body-bold">End date:</TrueImpactText>
                <KeyboardDatePicker
                  format={'dd MMM yyyy'}
                  placeholder="Select a end date"
                  value={value?.endDate ?? null}
                  onChange={handleEndDate}
                  keyboardIcon={<FontAwesomeIcon icon={faCalendar} />}
                  PopoverProps={{
                    anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                    transformOrigin: { horizontal: 'center', vertical: 'top' },
                  }}
                />
              </div>
            </DateContainer>
          )}
        </div>
      </SectionBase>
    </LocalizationProvider>
  );
};

const ExpansionPanel = styled(ExpansionPanelMui)``;
const ExpansionPanelSummary = styled(ExpansionPanelSummaryMui)``;
const ExpansionPanelDetails = styled(ExpansionPanelDetailsMui)``;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACERS.medium};
`;

const SwitchToggleButton = styled(ToggleButton)`
  background-color: ${COLORS.greyLight} !important;
  text-transform: none !important;
  border: none !important;
  &:hover {
    background-color: ${COLORS.greyBase} !important;
  }
  &.Mui-selected {
    color: ${COLORS.whiteBase} !important;
    background-color: ${COLORS.ctaPrimary} !important;
    &:hover {
      background-color: ${COLORS.ctaPrimaryHover} !important;
    }
  }
`;

const SingleDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.tiny};
  .MuiFormControl-marginNormal {
    margin-top: 0;
  }
`;

const RENDERS_PERIOD = ['Other'];

const isOtherPeriodSelected = value =>
  RENDERS_PERIOD.indexOf(value?.value) > -1;
